import React, { useMemo } from 'react';

import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Alert, Flex, Form, Input, Select, Typography } from 'antd';
import { useFormikContext } from 'formik';

import { FormData } from '.';
import { usePartnerStats } from '../../api/getPartnerStats';
import { reasons } from '../../types';

const { Text } = Typography;
const { TextArea } = Input;

export const getReasonLabel = (reason: string): string => {
  switch (reason) {
    case reasons.CreatedByError:
      return 'Organization was created by error';
    case reasons.EndOfContract:
      return 'End of contract';
    case reasons.ClientRequest:
      return "Client's demand";
    case reasons.BillNotPaid:
      return 'Bill have not been paid';
    default:
      return reason;
  }
};

export const FormContent = ({ isPending }) => {
  const { data: partnerStats } = usePartnerStats({});

  const {
    values: { reason },
    errors,
    isSubmitting,
    setFieldValue,
  } = useFormikContext<FormData>();

  const partnerHasProductsToReturn = (partnerStats?.inBusiness.value || 0) > 0;

  const options = useMemo(
    () => [
      {
        value: reasons.CreatedByError,
        label: getReasonLabel(reasons.CreatedByError),
        disabled: partnerHasProductsToReturn,
      },
      {
        value: reasons.EndOfContract,
        label: getReasonLabel(reasons.EndOfContract),
      },
      {
        value: reasons.ClientRequest,
        label: getReasonLabel(reasons.ClientRequest),
      },
      {
        value: reasons.BillNotPaid,
        label: getReasonLabel(reasons.BillNotPaid),
      },
    ],
    [partnerHasProductsToReturn]
  );

  return (
    <Form
      style={{
        width: '100%',
      }}
    >
      <Flex
        gap=".75rem"
        vertical
        style={{
          width: '100%',
          padding: '1.5rem',
          background: 'white',
          borderRadius: '.5rem',
          boxShadow: '0 .25rem 1rem 0 rgba(0, 0, 0, 0.08)',
        }}
      >
        <Text
          style={{
            fontSize: '.875rem',
            fontWeight: '500',
            lineHeight: '125%',
          }}
        >
          Please, indicate the reason:
        </Text>
        <Flex vertical style={{ width: '100%' }}>
          <Form.Item
            style={{ width: '100%' }}
            name="reason"
            validateStatus={errors.reason ? 'error' : undefined}
            help={errors.reason}
          >
            <Select
              status={errors.reason ? 'error' : undefined}
              disabled={isPending || isSubmitting}
              placeholder="The reason"
              style={{
                width: '100%',
              }}
              onChange={(newReason) => {
                setFieldValue('reason', newReason, true);
              }}
              options={options}
            />
          </Form.Item>
          {reason &&
            reason !== reasons.CreatedByError &&
            partnerStats?.cannotClosePartner && (
              <TextArea
                name="details"
                disabled={isPending || isSubmitting}
                onChange={(e) => setFieldValue('details', e.target.value)}
                placeholder="Comments"
                autoSize={{ minRows: 3, maxRows: 5 }}
              />
            )}
        </Flex>
        {reason && reason !== reasons.CreatedByError && (
          <>
            <Alert
              style={{
                width: '100%',
              }}
              description={
                <ul
                  style={{
                    listStyleType: 'disc',
                    paddingLeft: '1.5rem',
                    color: '#0C6B64',
                  }}
                >
                  <li>
                    Users will be notified about the organisation’s closure
                  </li>
                  <li>
                    They will be also kindly asked to return all their
                    LivingPackets products back to us
                  </li>
                </ul>
              }
              type="info"
              showIcon
              icon={
                <ExclamationCircleOutlined
                  style={{
                    color: '#0C6B64',
                  }}
                />
              }
            />
            {partnerStats?.canClosePartner && (
              <Form.Item
                layout="vertical"
                label="Please enter the Partnership ID to confirm the closure"
                name="confirmPartnerId"
                style={{ width: '100%' }}
                validateStatus={errors.confirmPartnerId ? 'error' : undefined}
              >
                <Input
                  status={errors.confirmPartnerId ? 'error' : undefined}
                  disabled={isPending || isSubmitting}
                  placeholder="Please enter the Partnership ID to confirm the closure"
                  onChange={(e) =>
                    setFieldValue('confirmPartnerId', e.target.value, true)
                  }
                />
              </Form.Item>
            )}
          </>
        )}
      </Flex>
    </Form>
  );
};
